/**
 * Page /signaler/:slug — Signalement public anonyme (Phase 2A core L9).
 *
 * Aucune auth requise. La sécurité vient du backend (rate-limit IP +
 * validation Zod + code suivi 48 bits + RLS auth_bypass).
 *
 * Flow 4 étapes (référentiel mairie-collectivite-fr/services-techniques) :
 *   1. Catégorie + urgence (boutons icônes grandes)
 *   2. Description + localisation (texte + bouton "ma position" HTML5 geoloc)
 *   3. Identité minimale (email obligatoire, nom optionnel, CGU/PC)
 *   4. Confirmation + envoi → écran de succès avec code de suivi
 *
 * Design : mobile-first, dark mode supporté, cibles tactiles ≥ 44×44px.
 *
 * Refonte R11 (mai 2026) :
 *   - PageShell glass cohérent (LogoMark Landmark + halo tech + ThemeToggle)
 *   - Progress bar 4 barres avec couleur dynamique (success/tech/border)
 *   - Step 1 : 6 catégories en bento grid avec halos colorés selon sélection + 3 niveaux urgence avec mapping statique
 *   - Step 2 : form glass + bouton geoloc avec feedback success / warning
 *   - Step 3 : form glass + CGU/PC checkboxes accent-tech + bandeau info shieldcheck
 *   - Step 4 : RecapRow glass + bouton "Envoyer le signalement"
 *   - Écran succès : LogoMark CheckCircle2 + Card glass avec numéro/code copiable
 *
 * Logique métier 100% préservée :
 *   - useParams slug → api.public.tenants.list() + filter slug → tenantValid
 *   - api.public.signalements.create(slug, {sujet, description, categorie, urgence, ...})
 *   - HTML5 geolocation avec timeout 8s
 *   - 4 steps avec disabled conditions sur Suivant
 *   - cguAccepted + pcAccepted requis pour passer step 3 → 4
 *   - Format succès : codeSuivi + numero + suiviUrl
 *   - **Fix Tailwind purge-unsafe** : URGENCES.color → mapping statique URGENCE_STYLES
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import {
  Button, Input, Field, Card, Alert, Badge, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  Landmark, AlertTriangle, Construction, TreePine, Lightbulb,
  Trash2, Droplets, MoreHorizontal, MapPin, Check, ArrowRight,
  ArrowLeft, CheckCircle2, Copy, Mail, ShieldCheck, AlertCircle,
} from 'lucide-react';

type Categorie = 'voirie' | 'espaces_verts' | 'eclairage' | 'proprete' | 'assainissement' | 'autre';
type Urgence = 'normal' | 'rapide' | 'urgent';

const CATEGORIES: { value: Categorie; label: string; description: string; icon: typeof Construction }[] = [
  { value: 'voirie',         label: 'Voirie',         description: 'Nid de poule, dégradation, signalisation', icon: Construction },
  { value: 'espaces_verts',  label: 'Espaces verts',  description: 'Arbre, parc, herbe, élagage',              icon: TreePine },
  { value: 'eclairage',      label: 'Éclairage',      description: 'Lampadaire éteint, candélabre HS',         icon: Lightbulb },
  { value: 'proprete',       label: 'Propreté',       description: 'Dépôt sauvage, corbeille pleine',          icon: Trash2 },
  { value: 'assainissement', label: 'Assainissement', description: 'Eaux usées, grille bouchée',               icon: Droplets },
  { value: 'autre',          label: 'Autre',          description: 'Tout autre incident technique',            icon: MoreHorizontal },
];

// Mappings statiques URGENCE (Tailwind purge-safe)
const URGENCES: { value: Urgence; label: string; help: string; variant: 'urgent' | 'warning' | 'success' }[] = [
  { value: 'urgent', label: 'Urgent', help: 'Danger immédiat (chute, court-circuit, fuite)',           variant: 'urgent' },
  { value: 'rapide', label: 'Rapide', help: 'Gêne notable mais sans danger immédiat',                  variant: 'warning' },
  { value: 'normal', label: 'Normal', help: 'Pas d\'urgence, à traiter dans les jours/semaines à venir', variant: 'success' },
];

const URGENCE_STYLES: Record<Urgence, { border: string; bg: string }> = {
  urgent: { border: 'border-urgent', bg: 'bg-urgent/10' },
  rapide: { border: 'border-warning', bg: 'bg-warning/10' },
  normal: { border: 'border-success', bg: 'bg-success/10' },
};

// Topbar style partagé
const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

const TEXTAREA_CLS = 'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function SignalementPublicAnonymePage() {
  const { slug } = useParams<{ slug: string }>();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Form state
  const [categorie, setCategorie] = useState<Categorie | null>(null);
  const [urgence, setUrgence] = useState<Urgence>('normal');
  const [sujet, setSujet] = useState('');
  const [description, setDescription] = useState('');
  const [adresse, setAdresse] = useState('');
  const [quartier, setQuartier] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [nom, setNom] = useState('');
  const [cguAccepted, setCguAccepted] = useState(false);
  const [pcAccepted, setPcAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ codeSuivi: string; numero: string; suiviUrl: string } | null>(null);

  // Validation de tenant au montage
  const [tenantValid, setTenantValid] = useState<boolean | null>(null);
  const [tenantNom, setTenantNom] = useState<string>('');

  useEffect(() => {
    async function checkTenant() {
      if (!slug) {
        setTenantValid(false);
        return;
      }
      try {
        const res = await api.public.tenants();
        const found = res.items.find((t) => t.slug === slug);
        if (found) {
          setTenantValid(true);
          setTenantNom(found.nom);
        } else {
          setTenantValid(false);
        }
      } catch {
        setTenantValid(false);
      }
    }
    void checkTenant();
  }, [slug]);

  function useMyLocation() {
    if (!navigator.geolocation) {
      setGeoError('Votre navigateur ne supporte pas la géolocalisation.');
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setGeoLoading(false);
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError('Vous avez refusé l\'accès à votre position. Saisissez l\'adresse manuellement.');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGeoError('Position indisponible. Saisissez l\'adresse manuellement.');
        } else {
          setGeoError('Erreur de géolocalisation. Saisissez l\'adresse manuellement.');
        }
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  }

  async function submit() {
    if (!categorie || !slug) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.public.signalements.create(slug, {
        sujet: sujet.trim(),
        description: description.trim(),
        categorie,
        urgence,
        localisationAdresse: adresse.trim() || undefined,
        localisationQuartier: quartier.trim() || undefined,
        localisationLat: lat ?? undefined,
        localisationLng: lng ?? undefined,
        email: email.trim().toLowerCase(),
        nom: nom.trim() || undefined,
        cguAccepted: true as const,
      });
      setSuccess(result);
    } catch (err) {
      setError(err instanceof ApiException ? (err.payload.error || 'Erreur lors de l\'envoi') : 'Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  }

  // ============================================================================
  // ÉTATS SPÉCIAUX (chargement, erreur tenant, succès)
  // ============================================================================

  if (tenantValid === null) {
    return (
      <PageShell>
        <Card variant="glass" padded>
          <div className="py-12 text-center">
            <div
              className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-tech border-t-transparent"
              role="status"
              aria-live="polite"
            />
            <p className="mt-4 text-sm text-ink-muted">Vérification…</p>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (tenantValid === false) {
    return (
      <PageShell>
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <LogoMark icon={AlertTriangle} accent="urgent" size="lg" className="mx-auto" />
            <h1 className="mt-4 font-display text-xl font-semibold tracking-r-snug text-ink">
              Collectivité introuvable
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted leading-relaxed">
              Le lien que vous avez utilisé ne correspond à aucune collectivité référencée.
              Vérifiez l'URL transmise par votre mairie.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1 rounded-r-md px-1 text-sm text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à l'accueil
            </Link>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (success) {
    return (
      <PageShell tenantNom={tenantNom}>
        <Card variant="glass" padded>
          <div className="py-6 text-center">
            <LogoMark icon={CheckCircle2} accent="success" size="lg" className="mx-auto" />
            <SectionEyebrow as="div" className="mt-3 text-success">Confirmation</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-snug text-ink">
              Signalement enregistré
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted leading-relaxed">
              Votre signalement a été transmis aux services de <strong className="text-ink">{tenantNom}</strong>.
              Un email de confirmation vient d'être envoyé à <strong className="text-ink">{email}</strong>.
            </p>

            <div
              className="mx-auto mt-6 max-w-sm rounded-r-xl border border-border bg-surface/40 backdrop-blur-glass p-4 text-left shadow-soft"
              style={{
                backgroundImage:
                  'radial-gradient(160px 80px at 0% 0%, oklch(var(--c-success) / 0.14), transparent 70%), ' +
                  'linear-gradient(180deg, oklch(var(--c-surface) / 0.5), oklch(var(--c-bg-2) / 0.4))',
              }}
            >
              <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Numéro de référence</div>
              <div className="mt-1 font-mono text-base font-semibold text-ink">{success.numero}</div>

              <div className="mt-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Code de suivi</div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className="font-mono text-lg font-semibold tracking-wider text-tech-fg">{success.codeSuivi}</span>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(success.codeSuivi)}
                  className="rounded-r-md p-1.5 text-ink-muted transition-colors hover:bg-surface/60 hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  title="Copier le code"
                  aria-label="Copier le code de suivi"
                >
                  <Copy className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>

            <p className="mx-auto mt-4 max-w-md text-xs text-ink-muted">
              Conservez ce code pour suivre l'avancement de votre signalement.
            </p>

            <div className="mt-6 flex flex-col items-center gap-2">
              <Link to={`/suivi/${success.codeSuivi}`}>
                <Button variant="primary" size="md">Suivre mon signalement</Button>
              </Link>
              <Link
                to="/"
                className="rounded-r-md px-1 text-sm text-ink-muted hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </Card>
      </PageShell>
    );
  }

  // ============================================================================
  // STEPPER
  // ============================================================================

  return (
    <PageShell tenantNom={tenantNom}>
      {/* Progress bar */}
      <div
        className="mb-6 flex items-center justify-center gap-2"
        role="progressbar"
        aria-valuenow={step}
        aria-valuemin={1}
        aria-valuemax={4}
      >
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            className={[
              'h-2 w-12 rounded-full transition-colors',
              n < step
                ? 'bg-success'
                : n === step
                ? 'bg-tech shadow-glow-tech'
                : 'bg-border',
            ].join(' ')}
            aria-label={`Étape ${n}${n === step ? ' (en cours)' : n < step ? ' (terminée)' : ''}`}
          />
        ))}
      </div>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}

      {/* ÉTAPE 1 — Catégorie + urgence */}
      {step === 1 && (
        <Card variant="glass" padded={false}>
          <div className="p-5 sm:p-6">
            <SectionEyebrow as="div">Étape 1 sur 4</SectionEyebrow>
            <h2 className="mt-1 font-display text-lg font-semibold tracking-r-snug text-ink">
              Quel type de problème ?
            </h2>
            <p className="mt-1 text-sm text-ink-muted">Sélectionnez la catégorie qui correspond le mieux.</p>

            <div className="mt-4 grid grid-cols-2 gap-2.5">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const selected = categorie === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategorie(c.value)}
                    aria-pressed={selected}
                    className={[
                      'flex min-h-[88px] flex-col items-start gap-2 rounded-r-xl border-2 backdrop-blur-glass p-3 text-left transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                      selected
                        ? 'border-tech bg-tech/10 shadow-soft'
                        : 'border-border bg-surface/40 hover:border-tech/40 hover:bg-surface/60',
                    ].join(' ')}
                  >
                    <Icon
                      className={`h-6 w-6 ${selected ? 'text-tech-fg' : 'text-ink-muted'}`}
                      aria-hidden="true"
                    />
                    <div className="text-sm font-medium text-ink">{c.label}</div>
                    <div className="text-xs text-ink-muted leading-relaxed">{c.description}</div>
                  </button>
                );
              })}
            </div>

            {categorie && (
              <>
                <h3 className="mt-6 text-sm font-medium text-ink">Niveau d'urgence</h3>
                <div className="mt-2 space-y-2">
                  {URGENCES.map((u) => {
                    const selected = urgence === u.value;
                    const styles = URGENCE_STYLES[u.value];
                    return (
                      <button
                        key={u.value}
                        type="button"
                        onClick={() => setUrgence(u.value)}
                        aria-pressed={selected}
                        className={[
                          'flex w-full items-center gap-3 rounded-r-lg border-2 backdrop-blur-glass p-3 text-left transition-all',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                          selected
                            ? `${styles.border} ${styles.bg}`
                            : 'border-border bg-surface/40 hover:border-tech/40 hover:bg-surface/60',
                        ].join(' ')}
                      >
                        <Badge variant={u.variant} size="sm">{u.label}</Badge>
                        <span className="flex-1 text-xs text-ink-muted leading-relaxed">{u.help}</span>
                        {selected && <Check className="h-4 w-4 text-tech-fg" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end border-t border-border bg-surface/30 backdrop-blur-glass p-4">
            <Button variant="primary" disabled={!categorie} onClick={() => setStep(2)}>
              Suivant <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </Card>
      )}

      {/* ÉTAPE 2 — Description + localisation */}
      {step === 2 && (
        <Card variant="glass" padded={false}>
          <div className="space-y-4 p-5 sm:p-6">
            <div>
              <SectionEyebrow as="div">Étape 2 sur 4</SectionEyebrow>
              <h2 className="mt-1 font-display text-lg font-semibold tracking-r-snug text-ink">
                Décrivez le problème
              </h2>
            </div>

            <Field label="Titre court" htmlFor="sujet" required>
              <Input
                id="sujet"
                placeholder="Ex: Nid de poule rue de la République"
                value={sujet}
                onChange={(e) => setSujet(e.target.value)}
                maxLength={200}
                required
              />
            </Field>

            <Field
              label="Description détaillée"
              htmlFor="description"
              required
              hint="Minimum 10 caractères. Décrivez ce que vous avez constaté, où exactement, depuis quand…"
            >
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                maxLength={5000}
                placeholder="Ex: Un trou profond d'environ 30cm s'est formé au croisement, dangereux pour les vélos. Présent depuis 3 jours."
                className={TEXTAREA_CLS}
                required
              />
            </Field>

            <h3 className="text-sm font-medium text-ink">Où se situe le problème ?</h3>

            <div className="rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3">
              {lat && lng ? (
                <div className="flex items-center gap-2 text-sm text-success">
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  <span>Position enregistrée : {lat.toFixed(5)}, {lng.toFixed(5)}</span>
                  <button
                    type="button"
                    onClick={() => { setLat(null); setLng(null); }}
                    className="ml-auto rounded-r-md px-1 text-xs text-ink-muted underline transition-colors hover:text-urgent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  >
                    Effacer
                  </button>
                </div>
              ) : (
                <Button type="button" variant="secondary" size="sm" onClick={useMyLocation} disabled={geoLoading}>
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  {geoLoading ? 'Localisation…' : 'Utiliser ma position'}
                </Button>
              )}
              {geoError && <p className="mt-2 text-xs text-warning">{geoError}</p>}
            </div>

            <Field label="Adresse / rue" htmlFor="adresse" hint="Saisissez l'adresse ou un point de repère.">
              <Input
                id="adresse"
                value={adresse}
                onChange={(e) => setAdresse(e.target.value)}
                placeholder="Ex: 12 rue de la République"
                maxLength={255}
              />
            </Field>

            <Field label="Quartier (optionnel)" htmlFor="quartier">
              <Input
                id="quartier"
                value={quartier}
                onChange={(e) => setQuartier(e.target.value)}
                placeholder="Ex: Centre-ville"
                maxLength={100}
              />
            </Field>
          </div>
          <div className="flex justify-between border-t border-border bg-surface/30 backdrop-blur-glass p-4">
            <Button type="button" variant="secondary" onClick={() => setStep(1)}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour
            </Button>
            <Button
              variant="primary"
              disabled={!sujet.trim() || description.trim().length < 10}
              onClick={() => setStep(3)}
            >
              Suivant <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </Card>
      )}

      {/* ÉTAPE 3 — Identité */}
      {step === 3 && (
        <Card variant="glass" padded={false}>
          <div className="space-y-4 p-5 sm:p-6">
            <div>
              <SectionEyebrow as="div">Étape 3 sur 4</SectionEyebrow>
              <h2 className="mt-1 font-display text-lg font-semibold tracking-r-snug text-ink">
                Vos coordonnées
              </h2>
              <p className="mt-1 text-sm text-ink-muted leading-relaxed">
                Votre email est nécessaire pour recevoir le numéro de suivi et être informé de l'avancement.
                Aucun compte n'est créé.
              </p>
            </div>

            <Field label="Email" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@email.fr"
                autoComplete="email"
                required
              />
            </Field>

            <Field label="Nom (optionnel)" htmlFor="nom" hint="Pour personnaliser les emails. Optionnel.">
              <Input
                id="nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Ex: Marie Dupont"
                autoComplete="name"
                maxLength={150}
              />
            </Field>

            <div className="space-y-2 border-t border-border pt-4">
              <label className="flex items-start gap-2 text-sm text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={cguAccepted}
                  onChange={(e) => setCguAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-tech"
                  required
                />
                <span>
                  J'accepte les{' '}
                  <Link
                    to="/legal/cgu"
                    target="_blank"
                    className="rounded-r-md px-0.5 text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  >
                    conditions d'utilisation
                  </Link>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-ink cursor-pointer">
                <input
                  type="checkbox"
                  checked={pcAccepted}
                  onChange={(e) => setPcAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-tech"
                  required
                />
                <span>
                  J'accepte la{' '}
                  <Link
                    to="/legal/politique-confidentialite"
                    target="_blank"
                    className="rounded-r-md px-0.5 text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                  >
                    politique de confidentialité
                  </Link>
                </span>
              </label>
            </div>

            <div className="rounded-r-lg border border-tech/30 bg-tech/5 backdrop-blur-glass p-3 text-xs text-ink-muted leading-relaxed">
              <ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-tech-fg align-text-bottom" aria-hidden="true" />
              Vos données sont transmises uniquement aux agents techniques de <strong className="text-ink">{tenantNom}</strong> pour le traitement de ce signalement. Aucune réutilisation commerciale.
            </div>
          </div>
          <div className="flex justify-between border-t border-border bg-surface/30 backdrop-blur-glass p-4">
            <Button type="button" variant="secondary" onClick={() => setStep(2)}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour
            </Button>
            <Button
              variant="primary"
              disabled={!email.trim() || !cguAccepted || !pcAccepted}
              onClick={() => setStep(4)}
            >
              Vérifier <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </Card>
      )}

      {/* ÉTAPE 4 — Confirmation + envoi */}
      {step === 4 && (
        <Card variant="glass" padded={false}>
          <div className="space-y-4 p-5 sm:p-6">
            <div>
              <SectionEyebrow as="div">Étape 4 sur 4</SectionEyebrow>
              <h2 className="mt-1 font-display text-lg font-semibold tracking-r-snug text-ink">
                Récapitulatif
              </h2>
            </div>

            <div className="space-y-3 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-4 text-sm">
              <RecapRow label="Catégorie" value={CATEGORIES.find((c) => c.value === categorie)?.label ?? '—'} />
              <RecapRow label="Urgence" value={URGENCES.find((u) => u.value === urgence)?.label ?? 'Normal'} />
              <RecapRow label="Titre" value={sujet} />
              <RecapRow label="Description" value={description} multiline />
              {(adresse || lat) && (
                <RecapRow
                  label="Localisation"
                  value={
                    [
                      adresse,
                      quartier ? `quartier ${quartier}` : null,
                      lat && lng ? `(${lat.toFixed(5)}, ${lng.toFixed(5)})` : null,
                    ].filter(Boolean).join(' — ')
                  }
                />
              )}
              <RecapRow label="Email" value={email} />
              {nom && <RecapRow label="Nom" value={nom} />}
            </div>

            <p className="text-xs text-ink-muted leading-relaxed">
              En envoyant, vous confirmez que les informations sont exactes. Vous recevrez immédiatement un email avec votre code de suivi.
            </p>
          </div>
          <div className="flex justify-between border-t border-border bg-surface/30 backdrop-blur-glass p-4">
            <Button type="button" variant="secondary" onClick={() => setStep(3)} disabled={submitting}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Modifier
            </Button>
            <Button variant="primary" onClick={submit} disabled={submitting}>
              {submitting ? (
                'Envoi…'
              ) : (
                <>
                  <Mail className="h-4 w-4" aria-hidden="true" /> Envoyer le signalement
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </PageShell>
  );
}

function PageShell({ children, tenantNom }: { children: React.ReactNode; tenantNom?: string }) {
  return (
    <div className="min-h-screen text-ink">
      <header
        className="sticky top-0 z-20 border-b border-border backdrop-blur-glass px-4 py-3"
        style={TOPBAR_BG}
      >
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <LogoMark icon={Landmark} accent="tech" size="md" aria-label="Mairie" />
          <div className="min-w-0 flex-1">
            <SectionEyebrow>{tenantNom ? 'Mairie de' : 'Service public'}</SectionEyebrow>
            <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
              {tenantNom ?? 'Signaler un incident'}
              {tenantNom && <span className="ml-1 text-ink-muted font-normal">· Signaler</span>}
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>
      <main id="main-content" className="mx-auto max-w-2xl px-4 py-6 sm:px-6 animate-fade-in">
        {children}
      </main>
    </div>
  );
}

function RecapRow({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div>
      <SectionEyebrow as="div">{label}</SectionEyebrow>
      <div className={`mt-0.5 text-ink ${multiline ? 'whitespace-pre-wrap leading-relaxed' : ''}`}>
        {value}
      </div>
    </div>
  );
}
